import React from "react";
import { Button } from "@/components/ui/button";
import { Download, Trash, FileText } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT");
  } catch {
    return iso?.slice(0, 10) || "";
  }
};

/** Tab "Preventivi" del Marketplace: lista con conversione in ordine e download PDF. */
export default function QuotesTable({ quotes, services, onConvertToOrder, onDelete }) {
  if (quotes.length === 0) {
    return (
      <div className="kpi-card text-center py-16">
        <FileText size={48} className="mx-auto mb-4 text-muted-foreground" weight="duotone" />
        <p className="text-muted-foreground">Nessun preventivo salvato.</p>
        <p className="text-xs text-muted-foreground mt-2">Genera un preventivo dalla tab Servizi.</p>
      </div>
    );
  }

  const downloadPdf = async (q) => {
    try {
      const r = await api.get(`/quotes/${q._id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Preventivo_${q.service_name.replace(/[^\w\s-]/g, "").slice(0, 40)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Errore download");
    }
  };

  return (
    <div className="space-y-2" data-testid="quotes-list">
      {quotes.map((q) => {
        const daysLeft = Math.max(0, Math.ceil((new Date(q.expires_at) - new Date()) / (1000 * 60 * 60 * 24)));
        return (
          <div
            key={q._id}
            className="kpi-card flex items-center justify-between gap-4 flex-wrap"
            data-testid={`quote-row-${q._id}`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <FileText size={26} color="#6D28D9" weight="duotone" />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{q.service_name}</div>
                <div className="text-xs text-muted-foreground truncate">{q.property_summary}</div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Emesso il {fmtDate(q.created_at)} · Scade il {fmtDate(q.expires_at)}
                  <span
                    className={`ml-2 px-1.5 py-0.5 rounded ${daysLeft <= 7 ? "bg-amber-100 text-amber-700" : "bg-purple-100 text-purple-700"}`}
                  >
                    {daysLeft} gg rimanenti
                  </span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono font-bold text-purple-700">{fmt(q.total_incl_vat)}</div>
              <div className="text-[10px] text-muted-foreground">IVA {q.vat_rate}% inclusa</div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                className="ht-gradient border-0 text-white h-8 px-3"
                onClick={() => {
                  const svc = services.find((s) => s._id === q.service_id);
                  if (!svc) {
                    toast.error("Servizio non più disponibile");
                    return;
                  }
                  onConvertToOrder(q, svc);
                }}
                data-testid={`convert-quote-${q._id}`}
              >
                Ordina servizio
              </Button>
              <Button variant="ghost" size="sm" onClick={() => downloadPdf(q)} data-testid={`download-quote-${q._id}`}>
                <Download size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => onDelete(q)}
                data-testid={`delete-quote-${q._id}`}
              >
                <Trash size={16} />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
